import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Tag } from "@/components/Layout";
import { AlertTriangle } from "lucide-react";
import useBulkSelection from "@/hooks/useBulkSelection";

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString();
}

function isAnomaly(row) {
  if (!row) return false;
  // Phase 84.63 — login-anomaly detection/flagging was removed; explicitly
  // exclude any legacy `login.suspicious` row even though old rows still
  // carry meta.severity="high" (the generic severity branch below would
  // otherwise still flag them).
  if (typeof row.action === "string" && row.action.startsWith("login.suspicious")) return false;
  // Phase 79 — surface source-download / exfil attempts as red anomalies.
  if (typeof row.action === "string" && row.action.startsWith("security.exfil.")) return true;
  const sev = row.meta && row.meta.severity;
  return sev === "high" || sev === "critical";
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const reload = () => api.get("/audit-log").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);

  const bulk = useBulkSelection({
    collection: "audit_log",
    ids: rows.map((r) => r.id),
    onDone: reload,
  });

  const anomalyCount = rows.filter(isAnomaly).length;
  const exfilCount = rows.filter((r) => typeof r.action === "string" && r.action.startsWith("security.exfil.")).length;
  const loginCount = anomalyCount - exfilCount;

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto fade-in" data-testid="audit-page">
      <PageHeader eyebrow="Trail" title="Audit log" subtitle="Every meaningful change in the system, with who did it and when." />

      {anomalyCount > 0 && (
        <div className="card-flat p-4 mb-4 border-l-4 border-l-red-500 bg-red-50" data-testid="audit-anomaly-banner">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle size={16} />
            <div className="text-sm">
              <span className="font-bold">
                {anomalyCount} anomal{anomalyCount === 1 ? "y" : "ies"} flagged
                {loginCount > 0 && exfilCount > 0 && (
                  <span className="font-normal text-red-600 ml-1">
                    ({loginCount} login · {exfilCount} source-download)
                  </span>
                )}
                {loginCount > 0 && exfilCount === 0 && <span className="font-normal text-red-600 ml-1">(login)</span>}
                {loginCount === 0 && exfilCount > 0 && <span className="font-normal text-red-600 ml-1">(source-download attempts)</span>}
              </span>
              <span className="ml-1 text-red-600">— review the highlighted rows below.</span>
            </div>
          </div>
        </div>
      )}

      <div className="card-flat">
        <div className={`grid ${bulk.canBulk ? "grid-cols-[32px_repeat(5,1fr)]" : "grid-cols-5"} gap-4 px-5 py-3 border-b border-slate-200 label-overline`}>
          {bulk.canBulk && <div><bulk.HeaderCheckbox /></div>}
          <div>When</div><div>Actor</div><div>Action</div><div>Entity</div><div>Meta</div>
        </div>
        <div className="divide-row max-h-[70vh] overflow-y-auto">
          {rows.map((r) => {
            const anomaly = isAnomaly(r);
            return (
              <div
                key={r.id}
                className={`grid ${bulk.canBulk ? "grid-cols-[32px_repeat(5,1fr)]" : "grid-cols-5"} gap-4 px-5 py-2 text-sm hover-row ${anomaly ? "bg-red-50 border-l-4 border-l-red-500" : ""} ${bulk.isSelected(r.id) ? "bg-orange-50/40" : ""}`}
                data-testid={`audit-row-${r.id}`}
              >
                {bulk.canBulk && <div><bulk.RowCheckbox id={r.id} /></div>}
                <div className={`text-xs ${anomaly ? "text-red-700 font-medium" : "text-slate-500"}`}>{fmt(r.ts)}</div>
                <div className={anomaly ? "text-red-800 font-semibold" : ""}>{r.actor_name}</div>
                <div>
                  {anomaly ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white rounded-sm" data-testid={`audit-anomaly-${r.id}`}>
                      <AlertTriangle size={10} /> {r.action}
                    </span>
                  ) : (
                    <Tag tone="brand">{r.action}</Tag>
                  )}
                </div>
                <div className={anomaly ? "text-red-700" : "text-slate-600"}>{r.entity_type}{r.entity_id ? ` · ${r.entity_id.slice(0, 8)}` : ""}</div>
                <div className={`text-xs font-mono truncate ${anomaly ? "text-red-700" : "text-slate-500"}`}>{JSON.stringify(r.meta || {})}</div>
              </div>
            );
          })}
          {rows.length === 0 && <div className="p-5 text-sm text-slate-500">No events yet.</div>}
        </div>
      </div>
      <bulk.ActionBar />
      <bulk.ConfirmDialog />
    </div>
  );
}
